import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, Receipt } from "lucide-react";
import { RewardsSection } from "@/components/RewardsSection";
import { EnquiriesPanel } from "@/components/EnquiryThread";
import { DisputeChat } from "@/components/DisputeChat";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const FULFILL_STYLE = {
  awaiting_dispatch: "text-amber-400 border-amber-400/40",
  dispatched: "text-[#007AFF] border-[#007AFF]/40",
  delivered: "text-emerald-400 border-emerald-400/40",
  disputed: "text-[#FF3B30] border-[#FF3B30]/40",
  overdue: "text-[#FF3B30] border-[#FF3B30]/40",
  refunded: "text-emerald-400 border-emerald-400/40",
};

function OrderActions({ o, role, onDone }) {
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/trust/orders/${o.id}/${path}`, body || {});
      toast.success("Updated");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };
  if (role !== "buyer" && o.fulfillment_status === "awaiting_dispatch") {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking / dispatch info"
          data-testid={`dispatch-input-${o.id}`}
          className="bg-black/40 border border-[#27272A] rounded-md text-xs px-3 py-2 flex-1 min-w-[180px]" />
        <button data-testid={`dispatch-btn-${o.id}`} disabled={busy || tracking.length < 2}
          onClick={() => act("dispatch", { tracking })}
          className="text-xs font-bold px-4 py-2 rounded-full bg-[#FF3B30] text-white hover:bg-[#e0342a] disabled:opacity-50">
          Mark dispatched
        </button>
        <span className="text-[10px] text-neutral-500">⏱ Dispatch within 48h or the order is flagged</span>
      </div>
    );
  }
  if (role === "buyer" && ["dispatched", "awaiting_dispatch"].includes(o.fulfillment_status)) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button data-testid={`confirm-btn-${o.id}`} disabled={busy} onClick={() => act("confirm")}
          className="text-xs font-bold px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25">
          ✓ Confirm delivery
        </button>
        <button data-testid={`dispute-btn-${o.id}`} disabled={busy}
          onClick={() => { const r = window.prompt("What went wrong with this order?"); if (r && r.length >= 5) act("dispute", { reason: r }); }}
          className="text-xs font-bold px-4 py-2 rounded-full border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10">
          Report a problem
        </button>
        {o.tracking && <span className="text-[10px] text-neutral-500">📦 {o.tracking}</span>}
      </div>
    );
  }
  if (["disputed", "refunded"].includes(o.fulfillment_status) || o.dispute_status) {
    return (
      <div className="mt-1">
        {o.fulfillment_status === "refunded" && (
          <div className="mt-3 text-xs text-emerald-400 font-semibold" data-testid={`refund-note-${o.id}`}>
            💸 Refunded to your original payment method
          </div>
        )}
        {o.fulfillment_status === "overdue" && role === "buyer" && (
          <div className="mt-3 text-xs text-amber-400 font-semibold">⏳ Seller missed the dispatch deadline — your refund is being processed automatically</div>
        )}
        <DisputeChat orderId={o.id} />
      </div>
    );
  }
  if (role === "buyer" && o.fulfillment_status === "overdue") {
    return (
      <div className="mt-3 text-xs text-amber-400 font-semibold" data-testid={`overdue-note-${o.id}`}>
        ⏳ Seller missed the 48h dispatch deadline — your refund is being processed automatically
      </div>
    );
  }
  return null;
}

const STATUS_STYLE = {
  completed: "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10",
  pending: "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10",
};

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/orders/mine");
      setOrders(data);
    } catch { setOrders([]); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="orders-page">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Purchase history</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Your orders</h1>

      <RewardsSection />

      {user && user !== false && (
        <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-5" data-testid="orders-enquiries">
          <EnquiriesPanel side={user.role === "buyer" ? "buyer" : "seller"} />
        </div>
      )}

      {orders === null ? (
        <div className="mt-10 text-neutral-500">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="mt-10 border border-dashed border-[#27272A] rounded-xl p-12 text-center">
          <Package className="h-8 w-8 text-neutral-600 mx-auto" />
          <div className="mt-3 font-bold text-lg">No orders yet</div>
          <div className="text-neutral-400 text-sm mt-2">Fresh drops land daily — grab them while they're hot.</div>
          <Link to="/discover" className="inline-block mt-5 px-5 h-11 leading-[44px] rounded-full bg-white text-black font-semibold text-sm hover:bg-neutral-200 transition-colors" data-testid="orders-browse-btn">
            Browse live drops
          </Link>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="rounded-xl border border-[#27272A] bg-[#121212] p-5" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <Link to={`/product/${o.product_id}`} className="font-bold text-white hover:text-[#007AFF] transition-colors">
                    {o.product_title}
                  </Link>
                  {user?.role !== "buyer" && o.side && (
                    <span data-testid={`order-side-${o.id}`} className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${o.side === "sold" ? "text-[#34C759] border-[#34C759]/40" : "text-[#007AFF] border-[#007AFF]/40"}`}>
                      {o.side === "sold" ? "You sold" : "You bought"}
                    </span>
                  )}
                  <div className="text-xs text-neutral-500 mt-1">
                    {new Date(o.placed_at).toLocaleString()} · qty {o.quantity}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[o.status] || STATUS_STYLE.pending}`} data-testid={`order-status-${o.id}`}>
                    {o.status}
                  </span>
                  {o.fulfillment_status && (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${FULFILL_STYLE[o.fulfillment_status] || "text-neutral-400 border-neutral-700"}`} data-testid={`order-fulfillment-${o.id}`}>
                      {o.fulfillment_status.replace("_", " ")}
                    </span>
                  )}
                  <span className="font-mono font-bold text-lg text-white">${o.total.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#27272A] flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] font-mono text-neutral-500">
                <span className="inline-flex items-center gap-1.5"><Receipt className="h-3 w-3" /> Receipt</span>
                {o.payment_reference && <span data-testid={`order-ref-${o.id}`}>ref: {o.payment_reference}</span>}
                {o.payment_provider && <span className="uppercase">{o.payment_provider}</span>}
                <span>order id: {o.id}</span>
              </div>
              <OrderActions o={o} role={o.side === "sold" ? "seller" : "buyer"} onDone={load} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
